<script>
  import Button from '$lib/components/Button.svelte';
  import Form from '$lib/components/form/Form.svelte';
  import MultiSelect from '$lib/components/form/MultiSelect.svelte';
  import Option from '$lib/components/form/Option.svelte';
  import Select from '$lib/components/form/Select.svelte';
  import TextArea from '$lib/components/form/TextArea.svelte';
  import { showToast } from '$lib/features/app/modals';
  import { ai } from '$lib/utils/ai';
  import { useQueryClient } from '@tanstack/svelte-query';
  import { onMount } from 'svelte';
  import { z } from 'zod';
  import { NOTE_TYPES } from '../../constants';
  import { useSaveNoteMutation } from '../../service';
  import { logger } from '$lib/logger';

  let {
    clientId,
    reportId = undefined,
    areaOptions = [],
    tall = false,
    rows = 5,
    onsave = undefined,
  } = $props();

  const note = '';
  const noteTypeOptions = Object.entries(NOTE_TYPES).map(([value, label]) => ({
    value,
    label,
  }));
  let newNote = $state('');
  let selectedType = $state(null);
  let selectedNoteAreas = $state([]);
  let validationErrors = $state({});

  let isFormDirty = $state(false);

  const queryClient = useQueryClient();
  const saveNoteMutation = useSaveNoteMutation();

  onMount(() => {
    if (note) {
      if (note.note) {
        newNote = note.note;
      }
      if (note.areas) {
        selectedNoteAreas = note.areas;
      }
    }
  });

  const formIsValid = () => {
    validationErrors = {};
    if (!newNote) {
      validationErrors.newNote = 'journalanteckning saknas';
    }
    if (!selectedType) {
      validationErrors.selectedType = 'Anteckningstyp saknas';
    }
    // if there are any errors, return false
    if (Object.keys(validationErrors).length > 0) {
      return false;
    }
    // otherwise, all is good
    return true;
  };

  const handleSubmit = () => {
    if (!formIsValid()) {
      return;
    }

    $saveNoteMutation.mutate(
      {
        clientId: Number(clientId),
        reportId,
        notes: newNote,
        consultationAreaIds: selectedNoteAreas,
        contactType: selectedType || 'None',
      },
      {
        onSuccess: () => {
          deleteDraftNote();
          showToast('Journalanteckning sparad', 'success');
          onsave?.();
          queryClient.invalidateQueries({ queryKey: ['notes'] });
        },
        onError: (error) => {
          logger.error('Failed to save note', {
            err: error instanceof Error ? error : undefined,
          });
          showToast(error.message, 'error');
        },
      },
    );
  };

  const deleteDraftNote = () => {
    newNote = '';
    selectedType = null;
    selectedNoteAreas = [];
  };

  let isHealthAreasSelectedByUser = $state(false);

  async function analyzeNoteForHealthAreas() {
    if (isHealthAreasSelectedByUser) return;
    if (!(newNote && areaOptions?.length)) return;

    try {
      const userPrompt = await ai.getPrompt('counselling-area-user-prompt', {
        note: newNote,
        healthAreas: JSON.stringify(areaOptions),
      });
      const response = await ai.generateText({
        system: ai.getPrompt('counselling-area'),
        messages: [
          {
            role: 'user',
            content: userPrompt,
          },
        ],
      });

      selectedNoteAreas = z.number().array().parse(JSON.parse(response));
    } catch (error) {
      logger.error('Error calling AI:', {
        err: error instanceof Error ? error : undefined,
      });
      showToast('Failed to resolve health areas', 'error');
    }
  }
</script>

<div>
  <Form onsubmit={handleSubmit} showConfirmationIfDirty bind:isFormDirty>
    <div class="relative">
      <TextArea
        name="newNote"
        {tall}
        bind:value={newNote}
        {rows}
        valid={!validationErrors.newNote}
        placeholder="Skriv en journalanteckning..."
        onFocusOut={analyzeNoteForHealthAreas}
      />
    </div>
    <div class="mt-2">
      <div class="flex flex-col justify-between gap-2">
        <div class="w-full">
          <MultiSelect
            name="selectedNoteAreas"
            placeholder="Hälsoområde"
            bind:value={selectedNoteAreas}
            onChange={(v) => (isHealthAreasSelectedByUser = !!v.length)}
          >
            {#if areaOptions}
              {#each areaOptions as option}
                <Option value={option.value}>{option.label}</Option>
              {/each}
            {/if}
          </MultiSelect>
        </div>
        <div class="w-full">
          <Select
            name="selectedType"
            valid={!validationErrors.selectedType}
            placeholder="Anteckningsttyp"
            bind:value={selectedType}
            options={noteTypeOptions}
          />
        </div>
        <div class="inline-block">
          <Button
            type="submit"
            iconLeft="save"
            fullWidth
            loading={$saveNoteMutation.isPending}
          >
            Spara
          </Button>
        </div>
      </div>
    </div>
  </Form>
</div>
